import React, { useState, useMemo, useEffect } from 'react';
import { useTasks } from '../hooks/useTasks';
import { usePermissions } from '../hooks/usePermissions';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Task, CreateTaskData, TaskStats } from '../types/task';
import CommonPageHeader from '../components/common/CommonPageHeader';
import TaskForm from '../components/tasks/TaskForm';
import TaskList from '../components/tasks/TaskList';
import TaskView from '../components/tasks/TaskView';
import { Card, CardContent, CardHeader } from '../components/common/Card';
import LoadingScreen from '../components/common/LoadingScreen';
import toast from 'react-hot-toast';
import {
  ClipboardDocumentListIcon,
  PlusIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  UserIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';

const Tasks: React.FC = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { hasAccess } = usePermissions();
  const {
    tasks,
    loading,
    createTask,
    updateTask,
    deleteTask,
    updateTaskStatus,
    updateTaskPriority,
    assignTask,
    unassignTask,
    getTaskStats,
    getMyTasks
  } = useTasks();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | undefined>(undefined);
  const [viewingTask, setViewingTask] = useState<Task | undefined>(undefined);
  const [activeTab, setActiveTab] = useState<'all' | 'my' | 'stats'>('all');

  // Effect to handle FAB trigger for new task
  useEffect(() => {
    const handleFABNewTask = () => {
      setSelectedTask(undefined);
      setIsFormOpen(true);
    };

    window.addEventListener('openTaskForm', handleFABNewTask);
    return () => {
      window.removeEventListener('openTaskForm', handleFABNewTask);
    };
  }, []);

  // Feladat statisztikák - hooks mindig futnak
  const taskStats = useMemo(() => getTaskStats(), [getTaskStats]);
  const myTasks = useMemo(() => getMyTasks(), [getMyTasks]);

  // Jogosultság ellenőrzés
  if (!hasAccess('tasks')) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">{t('common.errors.loadingFailed')}</div>
      </div>
    );
  }

  // Feladat létrehozása
  const handleCreateTask = async (data: CreateTaskData) => {
    try {
      await createTask(data);
      toast.success(t('tasks.messages.createSuccess'));
      setIsFormOpen(false);
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error(t('tasks.messages.createError'));
    }
  };

  // Feladat frissítése
  const handleUpdateTask = async (data: CreateTaskData) => {
    if (!selectedTask) return;
    
    try {
      await updateTask(selectedTask.id, data);
      toast.success(t('tasks.messages.updateSuccess'));
      setSelectedTask(undefined);
      setIsFormOpen(false);
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error(t('tasks.messages.updateError'));
    }
  };

  // Feladat törlése
  const handleDeleteTask = async (taskId: string) => {
    try {
      await deleteTask(taskId);
      toast.success(t('tasks.messages.deleteSuccess'));
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error(t('tasks.messages.deleteError'));
    }
  };

  // Feladat státusz frissítése
  const handleUpdateStatus = async (taskId: string, status: Task['status']) => {
    try {
      await updateTaskStatus(taskId, status);
      toast.success(t('tasks.messages.statusUpdateSuccess'));
    } catch (error) {
      console.error('Error updating task status:', error);
      toast.error(t('tasks.messages.statusUpdateError'));
    }
  };

  // Feladat prioritás frissítése
  const handleUpdatePriority = async (taskId: string, priority: Task['priority']) => {
    try {
      await updateTaskPriority(taskId, priority);
      toast.success(t('tasks.messages.priorityUpdateSuccess'));
    } catch (error) {
      console.error('Error updating task priority:', error);
      toast.error(t('tasks.messages.priorityUpdateError'));
    }
  };

  // Feladat hozzárendelése saját magunkhoz
  const handleAssignToMe = async (taskId: string) => {
    if (!user) return;
    
    try {
      await assignTask(taskId, user.id, user.displayName);
      toast.success(t('tasks.messages.assignSuccess'));
    } catch (error) {
      console.error('Error assigning task:', error);
      toast.error(t('tasks.messages.assignError'));
    }
  };

  // Feladat hozzárendelés eltávolítása
  const handleUnassign = async (taskId: string) => {
    try {
      await unassignTask(taskId);
      toast.success(t('tasks.messages.assignSuccess'));
    } catch (error) {
      console.error('Error unassigning task:', error);
      toast.error(t('tasks.messages.assignError'));
    }
  };

  // Feladat szerkesztése
  const handleEditTask = (task: Task) => {
    setSelectedTask(task);
    setIsFormOpen(true);
  };

  // Feladat megtekintése
  const handleViewTask = (task: Task) => {
    setViewingTask(task);
  };

  // Form bezárása
  const handleCloseForm = () => {
    setSelectedTask(undefined);
    setIsFormOpen(false);
  };

  // View bezárása
  const handleCloseView = () => {
    setViewingTask(undefined);
  };

  // Új feladat form
  const handleNewTask = () => {
    setSelectedTask(undefined);
    setIsFormOpen(true);
  };

  // Statisztikák renderelése
  const renderStats = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {/* Összes feladat */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ClipboardDocumentListIcon className="w-8 h-8 text-gray-400" />
            </div>
            <div className="ml-4">
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('tasks.stats.totalTasks')}</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{taskStats.total}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tennivaló */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ClockIcon className="w-8 h-8 text-gray-400" />
            </div>
            <div className="ml-4">
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('tasks.stats.todoTasks')}</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{taskStats.todo}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Folyamatban */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ChartBarIcon className="w-8 h-8 text-blue-500" />
            </div>
            <div className="ml-4">
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('tasks.stats.inProgressTasks')}</div>
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{taskStats.inProgress}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Befejezett */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircleIcon className="w-8 h-8 text-green-500" />
            </div>
            <div className="ml-4">
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('tasks.stats.completedTasks')}</div>
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">{taskStats.completed}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lejárt */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="w-8 h-8 text-red-500" />
            </div>
            <div className="ml-4">
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('tasks.stats.overdueTasks')}</div>
              <div className="text-2xl font-bold text-red-600 dark:text-red-400">{taskStats.overdue}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Ma esedékes */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CalendarIcon className="w-8 h-8 text-orange-500" />
            </div>
            <div className="ml-4">
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('tasks.filters.dueToday')}</div>
              <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">{taskStats.dueToday}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Héten esedékes */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CalendarIcon className="w-8 h-8 text-yellow-500" />
            </div>
            <div className="ml-4">
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('tasks.filters.dueThisWeek')}</div>
              <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{taskStats.dueThisWeek}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Saját feladatok */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <UserIcon className="w-8 h-8 text-primary-500" />
            </div>
            <div className="ml-4">
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('tasks.stats.myTasks')}</div>
              <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">{myTasks.length}</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Befejezési arány számítása
  const completionRate = taskStats.total > 0 ? Math.round((taskStats.completed / taskStats.total) * 100) : 0;

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="space-y-6">
      {/* Oldal fejléc */}
      <CommonPageHeader
        title={t('tasks.title')}
        subtitle={t('tasks.subtitle')}
        icon={ClipboardDocumentListIcon}
      >
        <button
          onClick={handleNewTask}
          className="bg-white/20 backdrop-blur-sm text-white px-3 py-2 rounded-md text-sm flex items-center justify-center hover:bg-white/30 transition-colors"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          <span>{t('tasks.create')}</span>
        </button>
      </CommonPageHeader>

      {/* Fő tartalom */}
      <div className="space-y-6">
        {/* Navigációs tabok */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('all')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'all'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              {t('tasks.filters.all')} ({tasks.length})
            </button>
            <button
              onClick={() => setActiveTab('my')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'my'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              {t('tasks.stats.myTasks')} ({myTasks.length})
            </button>
            <button
              onClick={() => setActiveTab('stats')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'stats'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              {t('nav.reports')}
            </button>
          </nav>
        </div>

        {/* Tartalom a kiválasztott tab alapján */}
        {activeTab === 'stats' && renderStats()}
        
        {activeTab === 'stats' && (
          <Card>
            <CardHeader>
              <div className="flex items-center">
                <ChartBarIcon className="w-5 h-5 text-gray-400 dark:text-gray-500 mr-2" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('tasks.stats.completionRate')}</h3>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <div className="flex-1">
                  <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400 mb-1">
                    <span>{t('tasks.stats.completedTasks')}</span>
                    <span>{completionRate}%</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div 
                      className="bg-primary-600 dark:bg-primary-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${completionRate}%` }}
                    />
                  </div>
                </div>
                <div className="ml-4 text-right">
                  <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">{taskStats.completed}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">/ {taskStats.total}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Feladatok listája */}
        {activeTab !== 'stats' && (
          <TaskList
            tasks={activeTab === 'my' ? myTasks : tasks}
            loading={loading}
            onEdit={handleEditTask}
            onDelete={handleDeleteTask}
            onUpdateStatus={handleUpdateStatus}
            onUpdatePriority={handleUpdatePriority}
            onAssignToMe={handleAssignToMe}
            onUnassign={handleUnassign}
            onView={handleViewTask}
          />
        )}
      </div>

      {/* Feladat form modal */}
      {isFormOpen && (
        <TaskForm
          isOpen={isFormOpen}
          onClose={handleCloseForm}
          onSubmit={selectedTask ? handleUpdateTask : handleCreateTask}
          initialData={selectedTask}
        />
      )}

      {/* Feladat view modal */}
      {viewingTask && (
        <TaskView
          task={viewingTask}
          isOpen={!!viewingTask}
          onClose={handleCloseView}
        />
      )}
    </div>
  );
};

export default Tasks; 